/**
 * Branded Nominal Types for V2 Integer ID Fields
 *
 * Prevents accidental cross-assignment of ID types (e.g., passing a BillId
 * where a SessionId is expected). Use constructor helpers at query boundaries
 * to cast plain numbers into branded types.
 *
 * @example
 * import { asBillId, asSessionId, type BillId } from '@/types/v2';
 * const billId = asBillId(42);
 * const sessionId = asSessionId(1);
 * // billId cannot be assigned to a SessionId parameter — compiler error
 */

declare const BillIdBrand: unique symbol;
declare const SessionIdBrand: unique symbol;
declare const LegislatorIdBrand: unique symbol;
declare const StateIdBrand: unique symbol;

export type BillId = number & { readonly [BillIdBrand]: typeof BillIdBrand };
export type SessionId = number & { readonly [SessionIdBrand]: typeof SessionIdBrand };
export type LegislatorId = number & { readonly [LegislatorIdBrand]: typeof LegislatorIdBrand };
export type StateId = number & { readonly [StateIdBrand]: typeof StateIdBrand };

/** Cast a plain number to BillId at query boundaries */
export function asBillId(n: number): BillId {
  return n as BillId;
}

/** Cast a plain number to SessionId at query boundaries */
export function asSessionId(n: number): SessionId {
  return n as SessionId;
}

/** Cast a plain number to LegislatorId at query boundaries */
export function asLegislatorId(n: number): LegislatorId {
  return n as LegislatorId;
}

/** Cast a plain number to StateId at query boundaries */
export function asStateId(n: number): StateId {
  return n as StateId;
}
